import type { LlmCostAccounting } from '@edgevanta/bid-tab-extract';
import type { LanguageModel } from 'ai';

import {
  type ItemMatch,
  type OwnerItemCandidate,
  resolveItemMatches,
} from '../item-resolve';
import {
  type CustomProposalStaging,
  type LineEnrichment,
  type StagingItem,
  mergeEnrichmentIntoStaging,
  walkStagingItems,
} from '../staging';
import { type ClassifyResolvers, classifyLines } from './classify-lines';

/** What `enrichStaging` needs from the repo: the owner catalog to match against,
 *  plus the shared classify resolvers. */
export interface StagingEnrichmentRepo extends ClassifyResolvers {
  listOwnerItems(ownerId: string): Promise<OwnerItemCandidate[]>;
}

export interface EnrichStagingResult {
  staging: CustomProposalStaging;
  summary: {
    total: number;
    matchedExisting: number;
    newItems: number;
    categorized: number;
    /** Lines classified THIS run (paid LLM calls). */
    classified: number;
    /** Lines whose prior resolution was reused (no repeat classification). */
    reused: number;
  };
}

/**
 * Run the on-the-fly hunts against the staging draft — item match, category,
 * collections, unit — and write the results back INTO staging (temporary store;
 * publish commits them). Uses the SAME identity rule + classify brain the
 * persisted path uses, so the preview equals what publish will do.
 */
export async function enrichStaging(
  staging: CustomProposalStaging,
  ownerId: string,
  repo: StagingEnrichmentRepo,
  options: {
    model?: LanguageModel;
    novelModel?: LanguageModel;
    costAccounting?: LlmCostAccounting;
    /** Reclassify every line even if it already carries a resolution. Default
     *  false: a second run only classifies lines that have never been resolved,
     *  reusing what's already in staging (no repeat paid LLM calls). */
    force?: boolean;
    /** Fired as enrichment ENTERS each of its two phases — `items` (the free
     *  deterministic catalog match) then `classification` (the paid LLM pass) —
     *  so a surface watching the run can show which half is working. */
    onPhase?: (phase: 'items' | 'classification') => void;
  } = {},
): Promise<EnrichStagingResult> {
  const lines = walkStagingItems(staging);
  if (lines.length === 0) {
    return {
      staging,
      summary: {
        total: 0,
        matchedExisting: 0,
        newItems: 0,
        categorized: 0,
        classified: 0,
        reused: 0,
      },
    };
  }

  // The item MATCH is deterministic + free, so always recompute it — the owner
  // catalog may have gained the line since the last run. Classification is the
  // PAID part, so skip lines already resolved unless forced.
  options.onPhase?.('items');
  const ownerItems = await repo.listOwnerItems(ownerId);
  const matches: ItemMatch[] = resolveItemMatches(
    lines.map((l) => ({
      description: l.description ?? null,
      itemNumber: l.itemNumber == null ? null : String(l.itemNumber),
      unit: l.unit ?? null,
    })),
    ownerItems,
  );

  // A line is "already resolved" once a prior pass classified it — i.e. it
  // carries a `categoryId`. `itemMatch` alone is NOT enough: a half-written line
  // can have `itemMatch: 'new'` with a null category, and keying the skip on
  // `itemMatch` would leave it forever unclassified (the classify branch never
  // runs, and the reused branch copies the line's own null category back).
  const needsClassify = (i: number) => options.force || !lines[i]?.categoryId;
  const toClassifyIdx = lines.flatMap((_l, i) => (needsClassify(i) ? [i] : []));

  options.onPhase?.('classification');
  const printedUnits = lines.map((line) => line.unit ?? '');
  const unitResolutionsPromise = repo.resolveUnitsByInput(printedUnits);
  const fresh = await classifyLines(
    toClassifyIdx.map((i) => ({
      description: lines[i]?.description ?? '',
      unit: lines[i]?.unit ?? null,
    })),
    repo,
    {
      costAccounting: options.costAccounting,
      model: options.model,
      novelModel: options.novelModel,
      unitResolutions: unitResolutionsPromise,
    },
  );
  const unitResolutions = await unitResolutionsPromise;
  const freshByIdx = new Map(toClassifyIdx.map((idx, j) => [idx, fresh[j]]));

  let classifiedCount = 0;
  let reused = 0;
  const enrichment: LineEnrichment[] = lines.map((line, i) => {
    const m = matches[i];
    const c = freshByIdx.get(i);
    if (!needsClassify(i)) {
      // Keep the classification already in staging; only refresh the (free)
      // match, which may have changed as the catalog grew. Collections carry
      // their resolved factor forward (decide-once — never re-derived here).
      reused += 1;
      const collections = line.collections ?? [];
      return {
        itemMatch: m?.match ?? line.itemMatch ?? 'new',
        itemId: m?.itemId ?? line.itemId ?? null,
        categoryId: line.categoryId ?? null,
        categoryCode: line.categoryCode ?? null,
        collectionCodes: line.collectionCodes ?? collections.map((c) => c.code),
        collections,
        unitId: line.unitId ?? null,
        isTrueLumpSum: line.isTrueLumpSum ?? false,
        liquidAcPattern: line.liquidAcPattern ?? null,
      };
    }
    classifiedCount += 1;
    // The classify brain already resolved each collection's conversion factor +
    // source UOM (`resolveCollectionFactor`); carry the WHOLE thing onto the
    // line, not just the code, so the factor survives to publish + is editable.
    const collections = (c?.collections ?? []).map((col) => ({
      code: col.code,
      factor: col.conversionFactor,
      sourceUom: col.sourceUom,
    }));
    return {
      itemMatch: m?.match ?? 'new',
      itemId: m?.itemId ?? null,
      categoryId: c?.categoryId ?? null,
      categoryCode: c?.categoryCode ?? null,
      collectionCodes: collections.map((col) => col.code),
      collections,
      unitId: c?.unitId ?? null,
      isTrueLumpSum: c?.isTrueLumpSum ?? false,
      liquidAcPattern: c?.liquidAcPattern ?? null,
    };
  });

  // UNIT is a deterministic catalog lookup, independent of the paid classify
  // skip: resolve every line's `unitId` from its printed unit on EVERY run and
  // override whatever the classify/reuse branch carried. This is free and lets
  // a catalog fix (e.g. newly-populated unit variants) heal already-classified
  // lines on a cheap re-run — the reuse branch alone could only echo a stale
  // null unitId.
  const withUnits = enrichment.map((e, i) => {
    const unit = lines[i]?.unit;
    return {
      ...e,
      unitId: (unit && unitResolutions.get(unit)?.id) || e.unitId,
    };
  });
  const unitCodes = new Map(
    [...unitResolutions].map(([input, resolution]) => [input, resolution.code]),
  );

  // Canonicalize each line's PRINTED unit ("L.F.") to the catalog code ("LF") so
  // the Items unit picker (options are codes) shows it as a selected value
  // instead of an empty box. Unknown units are left as-is.
  const canonicalized = canonicalizeUnits(
    mergeEnrichmentIntoStaging(staging, withUnits),
    unitCodes,
  );

  return {
    staging: canonicalized,
    summary: {
      total: lines.length,
      matchedExisting: enrichment.filter((e) => e.itemMatch === 'existing')
        .length,
      newItems: enrichment.filter((e) => e.itemMatch === 'new').length,
      categorized: enrichment.filter((e) => e.categoryId).length,
      classified: classifiedCount,
      reused,
    },
  };
}

/** Rewrite every line's printed `unit` to its canonical catalog code via the
 *  printed→code map. Pure — returns a new staging; unmapped units untouched. */
function canonicalizeUnits(
  staging: CustomProposalStaging,
  unitCodes: Map<string, string>,
): CustomProposalStaging {
  const fix = (item: StagingItem): StagingItem => {
    const code = item.unit && unitCodes.get(item.unit);
    return code ? { ...item, unit: code } : item;
  };
  return {
    ...staging,
    bidGroups: (staging.bidGroups ?? []).map((group) => ({
      ...group,
      schedules: group.schedules?.map((s) => ({
        ...s,
        items: (s.items ?? []).map(fix),
      })),
      items:
        group.schedules && group.schedules.length > 0
          ? group.items
          : group.items?.map(fix),
    })),
  };
}
